import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { useTitanApp } from "@/hooks/useTitanApp";
import { updateUserWeekAvailability } from "@/dispatchers/memberDispatchers";

// #region Interfaces
export interface WeeklySchedule {
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
  sunday: DayAvailability;
}

interface DayAvailability {
  enabled: boolean;
  slots: Slots[];
}
interface Slots {
  startTime: string;
  endTime: string;
}
// #endregion Interfaces

export const useAvailabilityProfileData = () => {
  const dayNames = [
    { key: "monday", name: "Monday" },
    { key: "tuesday", name: "Tuesday" },
    { key: "wednesday", name: "Wednesday" },
    { key: "thursday", name: "Thursday" },
    { key: "friday", name: "Friday" },
    { key: "saturday", name: "Saturday" },
    { key: "sunday", name: "Sunday" },
  ];
  const dayOfWeekMap: Record<number, keyof WeeklySchedule> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  };

  const { appState, memberState } = useTitanApp();
  const { darkMode } = appState || {};
  const { currentMember, isUserWeekAvailabilityLoading } = memberState || {};
  const { userWeekAvailability } = currentMember || {};
  const isDarkMode = darkMode ?? false;

  // #region Availability Validation Schema
  const hasOverlaps = (slots: { startTime: string; endTime: string }[]) => {
    const toMinutes = (time: string) => {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };
    const sorted = slots
      .map((s) => ({
        start: toMinutes(s.startTime),
        end: toMinutes(s.endTime),
      }))
      .sort((a, b) => a.start - b.start);

    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].end > sorted[i + 1].start) return true;
    }
    return false;
  };

  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

  const slotSchema = z
    .object({
      startTime: z.string().regex(timeRegex, "Invalid start time format"),
      endTime: z.string().regex(timeRegex, "Invalid end time format"),
    })
    .superRefine((slot, ctx) => {
      if (slot.startTime >= slot.endTime) {
        ctx.addIssue({
          path: ["endTime"],
          message: "Start time must be before end time",
          code: z.ZodIssueCode.custom,
        });
      }
    });

  const dayAvailabilitySchema = z
    .object({
      enabled: z.boolean(),
      slots: z.array(slotSchema),
    })
    .superRefine((data, ctx) => {
      if (data.slots.length > 0 && hasOverlaps(data.slots)) {
        ctx.addIssue({
          path: ["slots"],
          message: "Slots overlap or touch each other for this day",
          code: z.ZodIssueCode.custom,
        });
      }
    });
  const weeklyScheduleSchema = z.object({
    monday: dayAvailabilitySchema,
    tuesday: dayAvailabilitySchema,
    wednesday: dayAvailabilitySchema,
    thursday: dayAvailabilitySchema,
    friday: dayAvailabilitySchema,
    saturday: dayAvailabilitySchema,
    sunday: dayAvailabilitySchema,
  });
  // #endregion Availability Validation Schema

  // #region Availability functions
  const handleInitailizeWeeklySchedule = (
    userWeekAvailability: any[],
  ): WeeklySchedule => {
    const emptyDay: DayAvailability = { enabled: false, slots: [] };
    const schedule: WeeklySchedule = {
      monday: { ...emptyDay },
      tuesday: { ...emptyDay },
      wednesday: { ...emptyDay },
      thursday: { ...emptyDay },
      friday: { ...emptyDay },
      saturday: { ...emptyDay },
      sunday: { ...emptyDay },
    };

    if (!userWeekAvailability) return schedule;

    for (const day of userWeekAvailability) {
      const key = dayOfWeekMap[day.dayOfWeek];
      if (key) {
        schedule[key] = {
          enabled: day.slots?.length > 0,
          slots: day.slots ?? [],
        };
      }
    }
    return schedule;
  };
  const defaultValues = handleInitailizeWeeklySchedule(
    userWeekAvailability ?? [],
  );
  const form = useForm<WeeklySchedule>({
    defaultValues,
    resolver: zodResolver(weeklyScheduleSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { control, trigger, watch, handleSubmit, setValue, formState } = form;
  const { errors, isDirty, isValid } = formState;

  const handleToggleDay = (day: keyof WeeklySchedule, enabled: boolean) => {
    const dayValue = watch(day);
    const newDayValue: DayAvailability = {
      enabled,
      slots: enabled
        ? dayValue?.slots?.length
          ? dayValue.slots
          : [{ startTime: "09:00", endTime: "17:00" }]
        : [],
    };

    setValue(day, newDayValue, { shouldDirty: true, shouldValidate: true });
    trigger(day);
  };

  const handleAddSlot = (day: keyof WeeklySchedule) => {
    const dayValue = watch(day);
    const slots = dayValue?.slots ?? [];
    setValue(
      day,
      {
        ...dayValue,
        slots: [...slots, { startTime: "09:00", endTime: "17:00" }],
      } as DayAvailability,
      { shouldDirty: true, shouldValidate: true },
    );
    trigger(day);
  };
  const handleRemoveSlot = (day: keyof WeeklySchedule, index: number) => {
    const dayValue = watch(day);
    const updatedSlots = (dayValue?.slots ?? []).filter((_, i) => i !== index);
    setValue(day, { ...dayValue, slots: updatedSlots } as DayAvailability, {
      shouldDirty: true,
      shouldValidate: true,
    });
    trigger(day);
  };
  const handleUpdateSlot = (
    day: keyof WeeklySchedule,
    index: number,
    field: keyof Slots,
    value: string,
  ) => {
    const dayValue = watch(day);
    const updatedSlots = (dayValue?.slots ?? []).map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot,
    );
    setValue(day, { ...dayValue, slots: updatedSlots } as DayAvailability, {
      shouldDirty: true,
      shouldValidate: true,
    });
    trigger(day);
  };
  const onSubmit = (data: any) => {
    const userWeekAvailability = Object.entries(data)
      .map(([dayKey, value]) => {
        const day = value as DayAvailability;
        const dayOfWeek = Object.keys(dayOfWeekMap).find(
          (num) => dayOfWeekMap[Number(num)] === dayKey,
        );

        if (!dayOfWeek) return null;

        return {
          dayOfWeek: Number(dayOfWeek),
          slots: day.enabled ? day.slots : [],
        };
      })
      .filter((day) => day !== null);
    const payload = {
      EmailAddress: currentMember.emailAddress,
      UserWeekAvailability: userWeekAvailability,
    };
    updateUserWeekAvailability(payload);
  };
  //  #endregion

  // #region Return
  return {
    isDarkMode,
    isUserWeekAvailabilityLoading,
    dayNames,
    errors,
    isDirty,
    isValid,
    control,
    watch,
    handleToggleDay,
    handleAddSlot,
    handleUpdateSlot,
    handleRemoveSlot,
    handleSubmit,
    onSubmit,
  };
  //  #endregion
};
